import React, { useState } from "react";
import { useList } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import CreateProject from "./create";

interface Project {
  id: string;
  name: string;
  description: string;
  creator: string;
  imdfSet: string[];
  createdAt: string;
  updatedAt: string;
  lastModifiedBy: string;
  apiKey: string;
}

const ProjectListPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, error } = useList<Project>({
    resource: "projects",
    config: {
      pagination: {
        mode: "off",
      },
    },
  });

  const projects = data?.data ?? [];

  const formatImdfCount = (imdfSet: string[]): string => {
    return `${imdfSet.length} 個檔案`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("zh-TW", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleViewClick = (id: string): void => {
    window.location.href = `/projects/${id}`;
  };

  const handleEditClick = (id: string): void => {
    window.location.href = `/projects/${id}`;
  };

  const handleCreateClick = (): void => {
    setIsCreateModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-gray-500">
        無法載入資料，請稍後再試
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">專案列表</h2>
        <button
          onClick={handleCreateClick}
          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
        >
          建立新專案
        </button>
      </div>

      <div className="overflow-x-auto">
        {projects.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">目前沒有專案</p>
            <p className="text-sm text-gray-400 mt-2">
              點擊上方的「建立新專案」按鈕來創建專案
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  專案名稱
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  建立者
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  API Key
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  IMDF 數量
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  更新日期
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-b">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {projects.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div className="font-medium">{project.name}</div>
                      <div className="text-gray-500 text-xs mt-1 line-clamp-1">
                        {project.description || "無描述"}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {project.creator}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                    <div className="flex items-center">
                      <span className="truncate max-w-xs">
                        {project.apiKey}
                      </span>
                      <button
                        className="ml-2 text-blue-600 hover:text-blue-800"
                        onClick={() => {
                          navigator.clipboard.writeText(project.apiKey);
                          alert("API Key 已複製到剪貼簿");
                        }}
                        title="複製 API Key"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatImdfCount(project.imdfSet)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(project.updatedAt)}
                    <div className="text-gray-500 text-xs">
                      最後修改者: {project.lastModifiedBy}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewClick(project.id)}
                        className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-600 hover:border-blue-800 rounded-md transition-colors"
                      >
                        查看
                      </button>
                      <button
                        onClick={() => handleEditClick(project.id)}
                        className="px-3 py-1 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-600 hover:border-gray-800 rounded-md transition-colors"
                      >
                        編輯
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 建立專案對話框 */}
      <CreateProject
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </div>
  );
};

export default ProjectListPage;
